import * as yup from '@if/core/yup'
import { ControlledCheckbox } from '@if/mui'
import { Box, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'
import useFeatureFlags from '@src/data/api/featureSwitch-api/featureSwitch-api'
import { EMerchantProperties } from '@src/data/types/Constants'
import { FC, memo, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const propertiesConfig = [
  {
    key: EMerchantProperties.noPaymentPolicy,
    titleKey: 'merchant.noPaymentPolicy.title',
    descriptionKey: 'merchant.noPaymentPolicy.description',
  },
  {
    key: EMerchantProperties.hideAddress,
    titleKey: 'merchant.hideAddress.title',
    descriptionKey: 'merchant.hideAddress.description',
  },
  {
    key: EMerchantProperties.canSkipFlinks,
    titleKey: 'merchant.canSkipFlinks.title',
    descriptionKey: 'merchant.canSkipFlinks.description',
  },
  {
    key: EMerchantProperties.canPayBorrowers,
    titleKey: 'merchant.canPayBorrowers.title',
    descriptionKey: 'merchant.canPayBorrowers.description',
  },
  {
    key: EMerchantProperties.forcePaymentToBorrower,
    titleKey: 'merchant.forcePaymentToBorrower.title',
    descriptionKey: 'merchant.forcePaymentToBorrower.description',
  },
  {
    key: EMerchantProperties.automaticIncomeApproval,
    titleKey: 'merchant.automaticIncomeApproval.title',
    descriptionKey: 'merchant.automaticIncomeApproval.description',
  },
  {
    key: EMerchantProperties.canAcceptHighEstimate,
    titleKey: 'merchant.canAcceptHighEstimate.title',
    descriptionKey: 'merchant.canAcceptHighEstimate.description',
  },
  {
    key: EMerchantProperties.showInterestRateOnMerchantDashboard,
    titleKey: 'merchant.showInterestRate.title',
    descriptionKey: 'merchant.showInterestRate.description',
  },
] as const

const propertyConfigValidator = yup.boolean().required().default(false)

export const MerchantPropertiesSchema = yup.object().shape(
  propertiesConfig.reduce(
    (acc, curr) => {
      acc[curr.key] = propertyConfigValidator
      return acc
    },
    {} as Record<EMerchantProperties, typeof propertyConfigValidator>,
  ),
)

interface MerchantPropertiesPanelLabelProps {
  title: string
  description: string
}

const MerchantPropertiesPanelLabel = memo(({ title, description }: MerchantPropertiesPanelLabelProps) => {
  return (
    <Box>
      <Typography>{title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  )
})
MerchantPropertiesPanelLabel.displayName = 'MerchantPropertiesPanelLabel'

interface Props {
  worksheetByMerchant: boolean
}

const MerchantPropertiesPanel: FC<Props> = ({ worksheetByMerchant }) => {
  const { t } = useTranslation()
  const [featureFlags] = useFeatureFlags()
  const { control, setValue, watch } = useFormContext<yup.InferType<typeof MerchantPropertiesSchema>>()

  const canAcceptHighEstimate = watch(EMerchantProperties.canAcceptHighEstimate)
  const isReadOnlyCanPayBorrowers = watch(EMerchantProperties.forcePaymentToBorrower) === true || canAcceptHighEstimate
  const isWorksheetByMerchantActive = featureFlags?.useHybridFlowWorksheetByMerchant === true && worksheetByMerchant

  useEffect(() => {
    if (canAcceptHighEstimate) {
      setValue(EMerchantProperties.noPaymentPolicy, false)
      setValue(EMerchantProperties.canPayBorrowers, true)
    }
  }, [canAcceptHighEstimate, setValue])

  useEffect(() => {
    if (isWorksheetByMerchantActive) {
      setValue(EMerchantProperties.noPaymentPolicy, false)
    }
  }, [isWorksheetByMerchantActive, setValue])

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('merchant.properties')}
      </Typography>

      <Stack gap={1}>
        <ControlledCheckbox
          name={EMerchantProperties.noPaymentPolicy}
          control={control}
          disabled={isWorksheetByMerchantActive}
          label={
            <MerchantPropertiesPanelLabel
              title={t(propertiesConfig[0].titleKey)}
              description={t(propertiesConfig[0].descriptionKey)}
            />
          }
        />
        {featureFlags?.useHybridFlowWorksheetByMerchant && (
          <FormControlLabel
            control={<Checkbox checked={worksheetByMerchant} disabled />}
            label={
              <MerchantPropertiesPanelLabel
                title={t('merchant.worksheetByMerchant.title')}
                description={t('merchant.worksheetByMerchant.description')}
              />
            }
          />
        )}
        <ControlledCheckbox
          name={EMerchantProperties.hideAddress}
          control={control}
          label={
            <MerchantPropertiesPanelLabel
              title={t(propertiesConfig[1].titleKey)}
              description={t(propertiesConfig[1].descriptionKey)}
            />
          }
        />
        <ControlledCheckbox
          name={EMerchantProperties.canSkipFlinks}
          control={control}
          label={
            <MerchantPropertiesPanelLabel
              title={t(propertiesConfig[2].titleKey)}
              description={t(propertiesConfig[2].descriptionKey)}
            />
          }
        />
        <ControlledCheckbox
          name={EMerchantProperties.canPayBorrowers}
          control={control}
          disabled={isReadOnlyCanPayBorrowers}
          label={
            <MerchantPropertiesPanelLabel
              title={t(propertiesConfig[3].titleKey)}
              description={t(propertiesConfig[3].descriptionKey)}
            />
          }
        />
        <ControlledCheckbox
          name={EMerchantProperties.forcePaymentToBorrower}
          control={control}
          disabled={true}
          label={
            <MerchantPropertiesPanelLabel
              title={t(propertiesConfig[4].titleKey)}
              description={t(propertiesConfig[4].descriptionKey)}
            />
          }
        />
        <ControlledCheckbox
          name={EMerchantProperties.automaticIncomeApproval}
          control={control}
          label={
            <MerchantPropertiesPanelLabel
              title={t(propertiesConfig[5].titleKey)}
              description={t(propertiesConfig[5].descriptionKey)}
            />
          }
        />
        <ControlledCheckbox
          name={EMerchantProperties.canAcceptHighEstimate}
          control={control}
          label={
            <MerchantPropertiesPanelLabel
              title={t(propertiesConfig[6].titleKey)}
              description={t(propertiesConfig[6].descriptionKey)}
            />
          }
        />
        {featureFlags?.useMerchDBshowRate && (
          <ControlledCheckbox
            name={EMerchantProperties.showInterestRateOnMerchantDashboard}
            control={control}
            label={
              <MerchantPropertiesPanelLabel
                title={t(propertiesConfig[7].titleKey)}
                description={t(propertiesConfig[7].descriptionKey)}
              />
            }
          />
        )}
      </Stack>
    </Box>
  )
}

export default MerchantPropertiesPanel
